import React from "react";
import styled from "styled-components";

import { MgkPlan, formatMgkPrice } from "../mgkContent";

interface MgkPlanCardProps {
  plan: MgkPlan;
  selected: boolean;
  onSelect: () => void;
}

const Card = styled.button<{ $selected: boolean }>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  padding: 24px;
  border-radius: 12px;
  background-color: #fff;
  text-align: left;

  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--soremo-blue)" : "var(--soremo-border)")};
  box-shadow: ${({ $selected }) =>
    $selected ? "0 0 0 1px var(--soremo-blue), 2px 4px 12px rgba(0, 154, 206, 0.15)" : "none"};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: var(--soremo-blue);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

const Head = styled.div.attrs({ className: "u-row-between u-items-center u-mb8" })``;

const PlanName = styled.span`
  font-family: "din-2014-rounded-variable", sans-serif;
  font-variation-settings: "wght" 400;
  font-size: 1.5rem;
  line-height: 100%;
  letter-spacing: 0.05em;
  color: #000;
`;

const Check = styled.span.attrs({ className: "material-symbols-rounded" })<{ $selected: boolean }>`
  font-size: 24px;
  color: ${({ $selected }) => ($selected ? "var(--soremo-blue)" : "var(--soremo-border)")};
  font-variation-settings: "FILL" ${({ $selected }) => ($selected ? 1 : 0)};
`;

const Summary = styled.p.attrs({ className: "bodytext-13 u-text-light-gray u-mb16" })``;

const Features = styled.ul.attrs({ className: "u-col u-gap8 u-mb24" })`
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
`;

const Feature = styled.li.attrs({ className: "bodytext u-row u-items-center u-gap8" })`
  color: #000;
`;

const FeatureIcon = styled.span.attrs({ className: "material-symbols-rounded" })`
  font-size: 20px;
  color: var(--soremo-light-gray);
`;

const Price = styled.div.attrs({ className: "bodytext" })`
  color: #000;

  strong {
    font-family: "din-2014-rounded-variable", sans-serif;
    font-variation-settings: "wght" 400;
    font-size: 2rem;
    line-height: 100%;
    margin-right: 4px;
  }
`;

/**
 * 料金プランの選択カード（ラジオ相当）。
 */
const MgkPlanCard: React.FC<MgkPlanCardProps> = ({ plan, selected, onSelect }) => {
  return (
    <Card
      type="button"
      role="radio"
      aria-checked={selected}
      $selected={selected}
      onClick={onSelect}
    >
      <Head>
        <PlanName>{plan.name}</PlanName>
        <Check $selected={selected}>{selected ? "check_circle" : "radio_button_unchecked"}</Check>
      </Head>
      <Summary>{plan.summary}</Summary>
      <Features>
        {plan.features.map((f) => (
          <Feature key={f.text}>
            <FeatureIcon>{f.icon}</FeatureIcon>
            {f.text}
          </Feature>
        ))}
      </Features>
      <Price>
        {plan.monthlyPrice !== undefined ? (
          <>
            <strong>{plan.monthlyPrice.toLocaleString("ja-JP")}</strong>円（税込）/ 月
          </>
        ) : (
          formatMgkPrice(plan)
        )}
      </Price>
    </Card>
  );
};

export default MgkPlanCard;
